import React, { useEffect, useState } from "react";
import { DatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import Grid from "@material-ui/core/Grid";
import { scheduleFormStyles } from "../schedule.styles";
import { useDispatch, useSelector } from "react-redux";
import BaseTable from "../../shared-components/base-table/BaseTable";
import { Button, Icon, Typography } from "@material-ui/core";
import { Link, useHistory } from "react-router-dom";
import DateUtils from "app/utils/DateUtils";
import {
  FlightResultTypes,
  FlightResultTypess,
} from "../../../types/service-types/ScheduleTypes";
import { AppState } from "app/store";
import {
  fetchAllFlightResults,
  fetchAllReturnFlightResults,
  setSelectedFlight,
  setSelectedReturnFlight,
} from "../store/flight-results.slice";
import {
  handleScheduleFormChange,
  setScheduleForm,
} from "app/main/schedule/store/schedule-form.slice";
import LoadingButton from "../../shared-components/loading-button/LoadingButton";
import { warningMsg } from "../../../utils/MessageUtils";
import { WAY_TYPE } from "../../../constants/constants";
import FlightTakeoffIcon from "@material-ui/icons/FlightTakeoff";
import FlightLandIcon from "@material-ui/icons/FlightLand";
import FlightPricesComponent from "./FlightPricesComponent";
import { useTranslation } from "react-i18next";
import useAuthority from "../../../hooks/useAuthority";
import { ROLES } from "../../../constants/roles";
import Box from "@material-ui/core/Box";
import WarningIcon from "@material-ui/icons/Warning";

const ScheduleResultsPage = () => {
  const classes = scheduleFormStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const flightoffIcon = <FlightTakeoffIcon />;
  const flightIcon = <FlightLandIcon />;
  const { hasUserAnyAuthority } = useAuthority();

  const { flightResults, loading } = useSelector(
    (state: AppState) => state.schedule.flightResults
  );
  const { selectedFlight, selectedReturnFlight } = useSelector(
    (state: AppState) => state.schedule.flightResults
  );
  const { returnFlightResults, returnFlightResultsLoading } = useSelector(
    (state: AppState) => state.schedule.flightResults
  );
  const { scheduleForm } = useSelector(
    (state: AppState) => state.schedule.scheduleForm
  );
  const { wayType } = useSelector(
    (state: AppState) => state.schedule.scheduleForm
  );

  const [selectedDate, setSelectedDate] = React.useState(
    scheduleForm.dateOfDeparture
  );
  const [selectedReturnFlightDate, setSelectedReturnFlightDate] =
    React.useState(scheduleForm.dateOfReturnFlight);

  const [selectedTableFlight, setSelectedTableFlight] =
    useState<any>(selectedFlight);
  const [selectedTableReturnFlight, setSelectedTableReturnFlight] =
    useState<any>(selectedReturnFlight);

  // when user selects a departure flight row
  const onFlightSelect = (flight: FlightResultTypess) => {
    const totalPrice =
      flight.taxOfFlight +
      flight.availableSectorPrice.price.oneWayPrices.forAdults;

    setSelectedTableFlight({
      ...flight,
      tax: flight.taxOfFlight,
      adultDeparturePrice:
        flight.availableSectorPrice.price.oneWayPrices.forAdults,
      adultReturnPrice:
        flight.availableSectorPrice.price.returnPrices.forAdults,
      currencySymbol: flight.currency.symbol,
      totalPrice,
    });

    // when changing departure flight, clear old return selection
    setSelectedTableReturnFlight(null);
  };

  // when user selects a return flight row (guarded by date validation with real Date objects)
  const onReturnFlightSelect = (flight: FlightResultTypess) => {
    if (selectedTableFlight) {
      const departureDateStr = selectedTableFlight.dateOfDeparture; // string from backend
      const returnDateStr = flight.dateOfDeparture; // string from backend

      const departureDate = DateUtils.getDateBy(departureDateStr) as Date;
      const returnDate = DateUtils.getDateBy(returnDateStr) as Date;

      // strict rule: return date must be AFTER departure date
      if (returnDate.getTime() <= departureDate.getTime()) {
        dispatch(
          warningMsg(
            localStorage.getItem("i18nextLng") === "en"
              ? "Return date must be after departure date."
              : "Data e kthimit duhet të jetë pas datës së nisjes."
          )
        );
        return; // do NOT update selectedTableReturnFlight
      }
    }

    const totalPrice =
      flight.taxOfFlight +
      flight.availableSectorPrice.price.returnPrices.forAdults;

    setSelectedTableReturnFlight({
      ...flight,
      tax: flight.taxOfFlight,
      adultReturnPrice: flight.availableSectorPrice.price.returnPrices.forAdults,
      currencySymbol: flight.currency.symbol,
      totalPrice,
    });
  };

  useEffect(() => {
    if (wayType === WAY_TYPE.RETURN_FLIGHT && selectedTableReturnFlight) {
      dispatch(setSelectedReturnFlight(selectedTableReturnFlight));
    }
  }, [selectedTableReturnFlight, dispatch, wayType]);

  useEffect(() => {
    if (selectedTableFlight) {
      dispatch(setSelectedFlight(selectedTableFlight));
    }
  }, [selectedTableFlight, dispatch]);

  const setFlights = () => {
    if (
      !selectedTableFlight ||
      (wayType === WAY_TYPE.RETURN_FLIGHT && !selectedTableReturnFlight)
    ) {
      dispatch(
        warningMsg(
          localStorage.getItem("i18nextLng") === "en"
            ? "You have not selected a flight!"
            : "Nuk keni selektuar fluturimin!"
        )
      );
      return;
    }

    // Extra safety: ensure return date > departure date before going to step 3
    if (wayType === WAY_TYPE.RETURN_FLIGHT) {
      const depStr = selectedTableFlight.dateOfDeparture;
      const retStr = selectedTableReturnFlight.dateOfDeparture;

      const depDate = DateUtils.getDateBy(depStr) as Date;
      const retDate = DateUtils.getDateBy(retStr) as Date;

      if (retDate.getTime() <= depDate.getTime()) {
        dispatch(
          warningMsg(
            localStorage.getItem("i18nextLng") === "en"
              ? "Return date must be after departure date."
              : "Data e kthimit duhet të jetë pas datës së nisjes."
          )
        );
        return;
      }
    }

    history.push("/schedule/step3");
  };

  const canSeeFreeSeats = (
    arrivalAirportName: string,
    arrivalAirportCode: any,
    numberOfAvailableSeats: any
  ) => {
    if (
      hasUserAnyAuthority([
        ROLES.ROLE_ADMIN,
        ROLES.ROLE_AGENT,
        ROLES.ROLE_SUB_AGENT,
      ])
    ) {
      return (
        <>
          {arrivalAirportName} {" ("}
          <b>{arrivalAirportCode}</b>
          {t("app.main.schedule.secondStep.tableRow1", ") only ")}{" "}
          {numberOfAvailableSeats}{" "}
          {t("app.main.schedule.secondStep.tableRow2", " free seats")}
        </>
      );
    } else if (hasUserAnyAuthority([ROLES.ROLE_AGENT])) {
      return (
        <>
          {arrivalAirportName} {"("}
          <b>{arrivalAirportCode}</b>
          {")"}
        </>
      );
    }
  };

  const columns = (wayType: string) => {
    return [
      {
        Header: "",
        accessor: "number",
        Cell: ({ cell }: any) => {
          const { number } = cell.row.original;
          return "#" + number;
        },
      },
      {
        Header: "",
        accessor: "departureAirport",
        Cell: ({ cell }: any) => {
          const {
            departureAirport,
            arrivalAirport,
            numberOfAvailableSeats,
          } = cell.row.original;
          return (
            <div>
              <span>
                <Icon
                  fontSize="small"
                  style={{
                    transform: "rotate(45deg)",
                    fontSize: "1.2rem",
                    marginBottom: "-4px",
                    marginRight: "2px",
                  }}
                >
                  flight
                </Icon>
              </span>
              {departureAirport.name} {" ("}
              <b>{departureAirport.code}</b>
              {") => "}
              <span>
                <Icon
                  fontSize="small"
                  style={{
                    transform: "rotate(135deg)",
                    fontSize: "1.2rem",
                    marginBottom: "-4px",
                    marginRight: "2px",
                  }}
                >
                  flight
                </Icon>
              </span>
              {canSeeFreeSeats(
                arrivalAirport.name,
                arrivalAirport.code,
                numberOfAvailableSeats
              )}
            </div>
          );
        },
      },
      {
        Header: "",
        accessor: "dateOfDeparture",
        Cell: ({ cell }: any) => {
          const { dateOfDeparture, timeOfDeparture } = cell.row.original;
          return DateUtils.toHumanDate(dateOfDeparture, timeOfDeparture);
        },
      },
      {
        Header: "",
        accessor: "availableSectorPrice",
        Cell: ({ cell }: any) => {
          const { currency, availableSectorPrice, taxOfFlight } =
            cell.row.original;
          if (wayType === WAY_TYPE.ONE_WAY) {
            return (
              availableSectorPrice.price.oneWayPrices.forAdults +
              taxOfFlight +
              " " +
              currency.symbol +
              `${t(
                "app.main.schedule.secondStep.tableRow3",
                " with tax"
              )}`
            );
          }
          return (
            availableSectorPrice.price.returnPrices.forAdults +
            taxOfFlight +
            " " +
            currency.symbol +
            `${t(
              "app.main.schedule.secondStep.tableRow3",
              " with tax"
            )}`
          );
        },
      },
    ];
  };

  const today = new Date();

  return (
    <div>
      
      {/* ONE WAY */}
      {wayType !== WAY_TYPE.RETURN_FLIGHT && (
        <>
          <div>
            
            <Grid
              container
              style={{ display: "flex", justifyContent: "space-between" }}
              spacing={2}
            >
             
              <Grid item xs={12}>
                
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <div style={{ display: "flex", marginTop: "10px" }}>
                  
                    <Typography style={{ margin: "0.5rem 1.2rem 0 0" }}>
                      {t(
                        "app.main.schedules.addPassenger.departureFlight",
                        "Departure flight must be selected first!"
                      )}
                    </Typography>
                    <DatePicker
                      disableToolbar
                      size={"small"}
                      minDate={today}
                      inputVariant="outlined"
                      autoOk
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="normal"
                      id="date-picker-inline"
                      value={selectedDate}
                      style={{ marginBottom: "20px", marginTop: 0 }}
                      onChange={(date) => {
                        // changing date should clear selected flight
                        setSelectedTableFlight(null);
                        setSelectedFlight(null as any);

                        if (date) {
                          setSelectedDate(date.toISOString());
                          dispatch(
                            setScheduleForm({
                              ...scheduleForm,
                              dateOfDeparture: date.toISOString(),
                            })
                          );
                          dispatch(
                            fetchAllFlightResults({
                              queryParams: {
                                oneWayPrice: scheduleForm.oneWayPrice,
                                returnPrice: scheduleForm.returnPrice,
                                departureAirportId:
                                  scheduleForm.departureAirportId,
                                arrivalAirportId: scheduleForm.arrivalAirportId,
                                dateOfDeparture: DateUtils.convertDate(
                                  date.toISOString()
                                ),
                              },
                            })
                          );
                        }
                      }}
                    />
                  </div>
                </MuiPickersUtilsProvider>
               
                <BaseTable
                  columns={columns(wayType)}
                  data={flightResults?.content}
                  onClickTableRow={onFlightSelect}
                  isLoading={loading}
                  disableRow={(row: FlightResultTypes) =>
                    scheduleForm.adult + scheduleForm.children >
                    row.numberOfAvailableSeats
                  }
                  selectedRow={(row: FlightResultTypes) =>
                    row.id === selectedTableFlight?.id
                  }
                />

                <div
                  style={{
                    marginBottom: "50px",
                    display: "flex",
                    flex: 1,
                    justifyContent: "flex-end",
                    padding: "15px",
                    background: "#e7e7e7",
                  }}
                >
                  <FlightPricesComponent
                    typeOfFlight={"ONE_WAY"}
                    flight={selectedTableFlight}
                    returnFlight={selectedTableReturnFlight}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </>
      )}

      {/* RETURN FLIGHT */}
      {wayType === WAY_TYPE.RETURN_FLIGHT && (
        <>
        {!selectedTableFlight && (
          <Box
            display="flex"
            alignItems="center"
            mb={2}
            p={1.5}
            borderRadius={4}
            style={{
              backgroundColor: "#f5f5f5",      
              borderLeft: "4px solid #ff9800",
            }}
          >
            <WarningIcon
              fontSize="small"
              style={{ marginRight: 8, color: "#ff9800" }}
            />
            <Typography variant="body2" color="textSecondary">
              {t(
                 "app.main.schedules.addPassenger.departureFlight",
                 "Departure time"
              )}
            </Typography>
          </Box>
        )}
          <div>
            <Grid
              container
              style={{ display: "flex", justifyContent: "space-between" }}
              spacing={2}
            >
              {/* DEPARTURE SIDE */}
              <Grid item xs={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <div style={{ display: "flex", marginTop: "10px" }}>
                    <Typography style={{ margin: "0.5rem 1.2rem 0 0" }}>
                      {t(
                        "app.main.schedules.addPassenger.departureTime",
                        "Departure time"
                      )}
                      :
                    </Typography>
                    <DatePicker
                      disableToolbar
                      size={"small"}
                      inputVariant="outlined"
                      autoOk
                      minDate={today}
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="normal"
                      id="date-picker-inline"
                      value={selectedDate}
                      style={{ marginBottom: "20px", marginTop: 0 }}
                      onChange={(date) => {
                        // user changed departure date after maybe selecting a return
                        setSelectedTableFlight(null);
                        setSelectedFlight(null as any);
                        setSelectedTableReturnFlight(null);
                        setSelectedReturnFlightDate(null as any);
                        dispatch(setSelectedReturnFlight(null as any));

                        if (date) {
                          setSelectedDate(date.toISOString());
                          dispatch(
                            handleScheduleFormChange({
                              field: "dateOfDeparture",
                              value: date.toISOString(),
                            })
                          );
                          // also clear dateOfReturnFlight in form – user must reselect it
                          dispatch(
                            handleScheduleFormChange({
                              field: "dateOfReturnFlight",
                              value: null,
                            })
                          );
                          dispatch(
                            fetchAllFlightResults({
                              queryParams: {
                                oneWayPrice: scheduleForm.oneWayPrice,
                                returnPrice: scheduleForm.returnPrice,
                                departureAirportId:
                                  scheduleForm.departureAirportId,
                                arrivalAirportId: scheduleForm.arrivalAirportId,
                                dateOfDeparture: DateUtils.convertDate(
                                  date.toISOString()
                                ),
                              },
                            })
                          );
                        }
                      }}
                    />
                  </div>
                </MuiPickersUtilsProvider>

                <BaseTable
                  columns={columns(wayType)}
                  data={flightResults?.content}
                  onClickTableRow={onFlightSelect}
                  isLoading={loading}
                  disableRow={(row: FlightResultTypes) =>
                    scheduleForm.adult + scheduleForm.children >
                    row.numberOfAvailableSeats
                  }
                  selectedRow={(row: FlightResultTypes) =>
                    row.id === selectedTableFlight?.id
                  }
                />
              </Grid>

              {/* RETURN SIDE */}
              <Grid item xs={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <div style={{ display: "flex", marginTop: "10px" }}>
                    <Typography style={{ margin: "0.5rem 1.2rem 0 0" }}>
                      {t(
                        "app.main.schedules.addPassenger.arrivalTime",
                        "Arrival Time"
                      )}
                      :
                    </Typography>
                    <DatePicker
                      size={"small"}
                      disableToolbar
                      inputVariant="outlined"
                      autoOk
                      minDate={selectedDate ? new Date(selectedDate) : today}
                      variant="inline"
                      format="dd/MM/yyyy"
                      margin="normal"
                      id="date-picker-inline"
                      value={selectedReturnFlightDate}
                      style={{ marginBottom: "20px", marginTop: 0 }}
                      onChange={(date) => {
                        // must have selected departure flight first
                        if (!selectedTableFlight) {
                          dispatch(
                            warningMsg(
                              localStorage.getItem("i18nextLng") === "en"
                                ? "Please select a departure flight first."
                                : "Ju lutem së pari zgjidhni fluturimin e nisjes."
                            )
                          );
                          return;
                        }

                        setSelectedReturnFlight(null);
                        if (date) {
                          setSelectedReturnFlightDate(date.toISOString());
                          dispatch(
                            handleScheduleFormChange({
                              field: "dateOfReturnFlight",
                              value: date.toISOString(),
                            })
                          );
                          dispatch(
                            fetchAllReturnFlightResults({
                              queryParams: {
                                oneWayPrice: scheduleForm.oneWayPrice,
                                returnPrice: scheduleForm.returnPrice,
                                departureAirportId:
                                  scheduleForm.arrivalAirportId,
                                arrivalAirportId:
                                  scheduleForm.departureAirportId,
                                dateOfDeparture: DateUtils.convertDate(
                                  date.toISOString()
                                ),
                              },
                            })
                          );
                        }
                      }}
                    />
                  </div>
                </MuiPickersUtilsProvider>

                <BaseTable
                  columns={columns(wayType)}
                  // only show return flights after a departure flight is selected
                  data={
                    selectedTableFlight
                      ? returnFlightResults?.content
                      : []
                  }
                  onClickTableRow={onReturnFlightSelect}
                  isLoading={returnFlightResultsLoading}
                  disableRow={(row: FlightResultTypes) => {
                    const seatsNotEnough =
                      scheduleForm.adult + scheduleForm.children >
                      row.numberOfAvailableSeats;

                    if (!selectedTableFlight) {
                      return true; // fully disabled when no departure flight selected
                    }

                    const departureDateStr =
                      selectedTableFlight.dateOfDeparture;
                    const returnDateStr = row.dateOfDeparture;

                    const departureDate = DateUtils.getDateBy(
                      departureDateStr
                    ) as Date;
                    const returnDate = DateUtils.getDateBy(
                      returnDateStr
                    ) as Date;

                    // strict: return date must be AFTER departure date
                    const invalidReturnDate =
                      returnDate.getTime() <= departureDate.getTime();

                    return seatsNotEnough || invalidReturnDate;
                  }}
                  selectedRow={(row: FlightResultTypes) =>
                    row.id === selectedTableReturnFlight?.id
                  }
                />
              </Grid>
            </Grid>
          </div>

          <div
            style={{
              display: "flex",
              flex: 1,
              justifyContent: "flex-end",
              padding: "15px",
              background: "#e7e7e7",
            }}
          >
            <FlightPricesComponent
              typeOfFlight={"RETURN_WAY"}
              flight={selectedTableFlight}
              returnFlight={selectedTableReturnFlight}
            />
          </div>
        </>
      )}

      <Grid
        container
        justify="space-between"
        alignItems="center"
        style={{ marginTop: "20px", marginBottom: "20px", padding: "5px" }}
      >
        <Link to="/schedule/step1">
          <Button style={{ background: "#e9e9e9" }}>
            {t(
              "app.main.schedule.scheduleForm.returnBackBtn",
              "Return Back"
            )}
          </Button>
        </Link>

        <LoadingButton
          btnTitle={t(
            "app.main.schedule.scheduleForm.continueBtn",
            "Continue"
          )}
          type="button"
          isLoading={false}
          variant="outlined"
          onClick={setFlights}
        />
      </Grid>
    </div>
  );
};

export default ScheduleResultsPage;
